import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Loader2, ArrowRight, RotateCcw, CreditCard, Factory, Truck, Check, XCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const STATUS_STYLE = {
  paid: "bg-green-100 text-green-700 border-green-300",
  pending: "bg-yellow-100 text-yellow-700 border-yellow-300",
  expired: "bg-red-100 text-red-700 border-red-300",
};

const CARRIER_TRACKING = {
  "Canada Post": "https://www.canadapost-postescanada.ca/track-reperage/en#/search?searchFor={t}",
  UPS: "https://www.ups.com/track?tracknum={t}",
  FedEx: "https://www.fedex.com/fedextrack/?trknbr={t}",
  Purolator: "https://www.purolator.com/en/shipping/tracker?pin={t}",
  DHL: "https://www.dhl.com/en/express/tracking.html?AWB={t}",
};

const trackingUrl = (carrier, tracking) => {
  const tmpl = CARRIER_TRACKING[carrier || ""];
  return tmpl && tracking ? tmpl.replace("{t}", encodeURIComponent(tracking)) : null;
};

const fmtShipDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

// Customer-facing order progress: Paid → In production → Shipped.
function OrderTracker({ order }) {
  if (order.status !== "paid") return null;
  const fs = order.fulfillment_status || "pending_production";
  if (fs === "canceled") {
    return (
      <div className="flex items-center gap-2 text-sm font-bold text-red-600 bg-red-50 nb-border border-red-200 rounded-xl px-3 py-2 mb-4" data-testid="order-tracker-canceled">
        <XCircle size={16} /> This order was canceled.
      </div>
    );
  }
  const steps = [
    { key: "paid", label: "Paid", icon: CreditCard },
    { key: "in_production", label: "In production", icon: Factory },
    { key: "complete", label: "Shipped", icon: Truck },
  ];
  const activeIndex = fs === "complete" ? 2 : fs === "in_production" ? 1 : 0;
  const track = trackingUrl(order.carrier, order.tracking_number);
  const shipDate = fmtShipDate(order.ship_date);
  return (
    <div className="mb-5" data-testid="order-tracker">
      <div className="flex items-center">
        {steps.map((s, i) => {
          const reached = i <= activeIndex;
          const current = i === activeIndex;
          const done = i < activeIndex;
          const Icon = done ? Check : s.icon;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center gap-1 shrink-0" data-testid={`tracker-step-${s.key}`} data-active={reached}>
                <div
                  className={`w-10 h-10 rounded-full nb-border flex items-center justify-center transition-colors ${
                    reached ? "bg-nbpink text-white" : "bg-white text-nbblack/30"
                  } ${current ? "nb-shadow" : ""}`}
                >
                  <Icon size={18} />
                </div>
                <span className={`text-[11px] font-bold whitespace-nowrap ${reached ? "text-nbblack" : "text-nbblack/40"}`}>{s.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-1 rounded-full mx-1 mb-4 ${i < activeIndex ? "bg-nbpink" : "bg-nbblack/15"}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {fs === "complete" && order.tracking_number && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm bg-green-50 nb-border border-green-200 rounded-xl px-3 py-2" data-testid="tracker-tracking">
          <Truck size={15} className="text-green-700 shrink-0" />
          <span className="font-bold text-green-800">Shipped via {order.carrier}</span>
          {track ? (
            <a href={track} target="_blank" rel="noreferrer" data-testid="tracker-track-link" className="inline-flex items-center gap-1 font-bold text-nbpink hover:underline">
              Track {order.tracking_number} <ArrowRight size={14} />
            </a>
          ) : (
            <span className="font-semibold text-nbblack/70">Tracking: {order.tracking_number}</span>
          )}
        </div>
      )}

      {fs !== "complete" && shipDate && (
        <div className="mt-2 inline-flex items-center gap-2 text-sm bg-nbyellow/40 nb-border rounded-xl px-3 py-1.5" data-testid="tracker-ships-by">
          <Truck size={15} className="text-nbpink shrink-0" />
          <span className="font-bold">Ships by <span className="text-nbpink" data-testid="tracker-ship-date">{shipDate}</span></span>
        </div>
      )}
    </div>
  );
}

export default function Orders() {
  const { user, loading: authLoading } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);

  const reorder = (o) => {
    (o.items || []).forEach((it) => {
      addItem({
        sticker_id: it.sticker_id,
        preview_path: it.preview_path,
        original_path: it.original_path,
        holo_path: it.holo_path,
        name: it.name || "Custom Sticker",
        width_in: it.width_in,
        height_in: it.height_in,
        material_key: it.material_key,
        material_name: it.material_name,
        cut_type: it.cut_type,
        cut_name: it.cut_name,
        shape: it.shape || "contour_cut",
        offset_in: it.offset_in,
        quantity: it.quantity,
        per_sheet: it.per_sheet,
        total_stickers: it.total_stickers,
        message: it.message || "",
        unit_price: it.unit_price,
        line_total: it.line_total,
      });
    });
    toast.success("Added to cart — prices are re-checked at checkout.");
    navigate("/cart");
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setOrders([]);
      return;
    }
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  }, [user, authLoading]);

  if (authLoading || orders === null) {
    return (
      <div className="App">
        <Navbar />
        <div className="flex justify-center py-32"><Loader2 className="animate-spin" size={40} /></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="App">
        <Navbar />
        <div className="max-w-lg mx-auto px-5 py-20 text-center">
          <p className="font-head font-bold text-xl mb-6">Please sign in to view your orders.</p>
          <Link to="/auth" className="inline-flex items-center gap-2 bg-nbpink text-white font-head font-bold px-6 py-3 rounded-full nb-border nb-shadow nb-press">
            Sign in <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-head font-extrabold text-4xl sm:text-5xl mb-8">Your orders</h1>
        {orders.length === 0 ? (
          <div className="bg-white nb-border nb-shadow rounded-3xl p-14 text-center">
            <Package size={56} className="mx-auto mb-4 text-nbblack/30" />
            <p className="font-head font-bold text-xl mb-6">No orders yet.</p>
            <Link to="/design" className="inline-flex items-center gap-2 bg-nbpink text-white font-head font-bold px-6 py-3 rounded-full nb-border nb-shadow nb-press">
              Create a sticker <ArrowRight size={18} />
            </Link>
          </div>
        ) : (
          <div className="space-y-5">
            {orders.map((o) => (
              <div key={o.id} data-testid="order-row" className="bg-white nb-border nb-shadow rounded-2xl p-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <p className="font-head font-bold text-lg">Order {o.display_id || `#${o.id.slice(0, 8)}`}</p>
                    <p className="text-sm text-nbblack/50">{new Date(o.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 ${STATUS_STYLE[o.status] || STATUS_STYLE.pending}`}>
                      {o.status === "paid" && o.fulfillment_status ? o.fulfillment_status.replace(/_/g, " ") : o.status}
                    </span>
                    <span className="font-head font-extrabold text-2xl">${o.total.toFixed(2)}</span>
                  </div>
                </div>
                <OrderTracker order={o} />
                <div className="flex gap-3 flex-wrap">
                  {o.items.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-[#FFF9F0] rounded-xl nb-border p-2 pr-4">
                      <div className="w-12 h-12 rounded-lg bg-white nb-border overflow-hidden flex items-center justify-center">
                        {it.preview_path && <img src={fileUrl(it.preview_path)} alt="" className="w-full h-full object-contain p-0.5" />}
                      </div>
                      <div className="text-sm">
                        <p className="font-bold">{it.material_name} · {it.cut_name}</p>
                        <p className="text-nbblack/60">{it.quantity} pcs · {it.width_in}"×{it.height_in}"</p>
                        {it.message && <p className="text-xs text-nbblack/50 italic mt-0.5">“{it.message}”</p>}
                      </div>
                    </div>
                  ))}
                </div>
                {o.shipping?.full_name && (
                  <div className="mt-4 bg-[#FFF9F0] nb-border rounded-xl p-3 text-sm" data-testid="order-shipping">
                    <p className="font-head font-bold text-xs uppercase tracking-wide text-nbblack/50 mb-1">Ship to</p>
                    <p className="font-bold">{o.shipping.full_name}</p>
                    <p className="text-nbblack/70">
                      {o.shipping.address1}{o.shipping.address2 ? `, ${o.shipping.address2}` : ""}
                    </p>
                    <p className="text-nbblack/70">
                      {[o.shipping.city, o.shipping.province, o.shipping.postal_code].filter(Boolean).join(", ")}
                      {o.shipping.country ? ` · ${o.shipping.country}` : ""}
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 flex-wrap mt-4">
                  <span />
                  <button
                    onClick={() => reorder(o)}
                    data-testid="reorder-btn"
                    className="inline-flex items-center gap-2 bg-nbblack text-white font-head font-bold text-sm px-5 py-2 rounded-full nb-border nb-press"
                  >
                    <RotateCcw size={15} /> Re-order
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
